/**
 * Plan Tier Visual Configuration
 * 
 * This module defines the visual hierarchy and styling for different subscription plan tiers.
 * Each tier has distinct visual characteristics to communicate value and create aspirational desire.
 * 
 * Visual Design Philosophy:
 * - Basic/Starter: Calm, stable animations (green) - foundational tier
 * - Professional: Balanced, professional energy (purple) - mid-tier sweet spot
 * - Enterprise: High energy, premium feel (gold/amber) - top tier exclusivity
 */

import { PlanTier, TierVisualConfig } from '../components/ui/ElectricBorder';

/**
 * Tier Visual Configurations
 * Used by ElectricBorder and other animated components
 */
export const TIER_CONFIGS: Record<PlanTier, TierVisualConfig> = {
  basic: {
    color: '#10B981',
    speed: 0.8,
    chaos: 0.4,
    thickness: 2,
  },
  professional: {
    color: '#8B5CF6',
    speed: 1.2,
    chaos: 0.6,
    thickness: 2,
  },
  scale: {
    color: '#06B6D4',
    speed: 1.5,
    chaos: 0.8,
    thickness: 2,
  },
  enterprise: {
    color: '#F59E0B',
    speed: 1.8,
    chaos: 1.0,
    thickness: 3,
  },
};

/**
 * Maps plan IDs to their corresponding visual tier
 * @param planId - The subscription plan identifier
 * @returns The visual tier classification
 */
export const getPlanTier = (planId: string): PlanTier => {
  const lowerPlanId = planId.toLowerCase();
  
  // Basic tier keywords
  if (lowerPlanId.includes('basic') || 
      lowerPlanId.includes('starter') || 
      lowerPlanId.includes('free') ||
      lowerPlanId.includes('lite')) {
    return 'basic';
  }
  
  // Enterprise tier keywords (gold/amber)
  if (lowerPlanId.includes('enterprise') ||
      lowerPlanId.includes('ultimate') ||
      lowerPlanId.includes('business')) {
    return 'enterprise';
  }

  // Scale tier keywords (cyan) - high-volume tier above professional
  if (lowerPlanId.includes('scale')) {
    return 'scale';
  }

  // Professional/Premium tier (purple) - mid-tier plans
  // (pro, professional, premium, standard, plus, etc.)
  return 'professional';
};

/**
 * Gets the primary color for a given tier
 * Useful for maintaining consistent theming across components
 * @param tier - The plan tier
 * @returns Hex color code
 */
export const getTierColor = (tier: PlanTier): string => {
  return TIER_CONFIGS[tier].color;
};

/**
 * Gets a human-readable tier name with proper formatting
 * @param tier - The plan tier
 * @returns Formatted tier name
 */
export const getTierDisplayName = (tier: PlanTier): string => {
  const names: Record<PlanTier, string> = {
    basic: 'Basic',
    professional: 'Professional',
    scale: 'Scale',
    enterprise: 'Enterprise',
  };
  return names[tier];
};

/**
 * Gets tier description for UI display
 * @param tier - The plan tier
 * @returns Tier description
 */
export const getTierDescription = (tier: PlanTier): string => {
  const descriptions: Record<PlanTier, string> = {
    basic: 'Perfect for getting started',
    professional: 'For growing businesses',
    scale: 'For high-volume, scaling teams',
    enterprise: 'Maximum power and flexibility',
  };
  return descriptions[tier];
};
